import { Dispatch, SetStateAction } from 'react';
import axios from 'axios';

import { AddableCourse, CourseLite, CourseOption } from '../types/course.response';
import { MutationEnvelope } from '../types/mutation.response';
import {
    userCoursesApi,
    CourseRequirementOverride,
} from '../services/api/user-courses.api';
import { userExamsApi } from '../services/api/user-exams.api';
import {
    getCourseId,
    getCourseTopic,
    getCourseCredit,
    getCourseKey,
    getCourseIsSaved,
    getCourseSemester,
    getUserItemId,
} from '../utils/courseUtils';
import { autoAssignSections } from '../utils/scheduleUtils';

export interface UseCourseActionsOptions {
    isLoggedIn: boolean;
    openLoginModal: () => void;
    userCourses: CourseOption[];
    optimisticFavorites: Map<string, boolean>;
    setOptimisticFavorites: Dispatch<SetStateAction<Map<string, boolean>>>;
    /** mutation 成功后回调,收到响应的 meta / 内嵌 fulfillments(契约 §4),供刷新编排收窄范围 */
    onSuccess: (outcome?: MutationEnvelope) => Promise<void> | void;
    onError?: (err: unknown, action: CourseActionKind) => void;
    rethrow?: boolean;
    /** 契约 §4.3:随 add/delete 请求内嵌详情档的 programIds(通常 = 已加载 program) */
    fulfillmentProgramIds?: () => string[];
}

export type CourseActionKind = 'add' | 'delete' | 'favorite';

export interface CourseActions {
    // 排课且未显式给 classSectionIds 时需要完整 CourseOption(autoAssignSections
    // 读 enrollGroups,调用方负责先补水);mark-as-taken(semester=null)摘要即可。
    // add/delete 返回 mutation 的 meta / 内嵌 fulfillments(未登录或错误被吞时 undefined)
    handleAddCourse: (
        courseOption: AddableCourse,
        overrides: CourseRequirementOverride[],
        semester: string | null,
        classSectionIds?: number[],
    ) => Promise<MutationEnvelope | undefined>;
    // delete/favorite 只读轻字段,摘要态(CourseSummary)即可直接操作
    handleDeleteCourse: (deletedCourse: CourseLite) => Promise<MutationEnvelope | undefined>;
    handleFavoriteCourse: (courseOption: CourseLite) => Promise<void>;
}

export function useCourseActions(options: UseCourseActionsOptions): CourseActions {
    const {
        isLoggedIn,
        openLoginModal,
        userCourses,
        optimisticFavorites,
        setOptimisticFavorites,
        onSuccess,
        onError,
        rethrow,
        fulfillmentProgramIds,
    } = options;

    const handleAddCourse: CourseActions['handleAddCourse'] = async (
        courseOption,
        overrides,
        semester,
        classSectionIds,
    ) => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }

        try {
            let outcome: MutationEnvelope;
            if (courseOption.type === 'EXAM') {
                const scoringType = courseOption.courseInfo.examScoringType;
                const score = scoringType === 'NUMERIC' ? 5 : scoringType === 'PASS_FAIL' ? 1 : null;
                const scoreAlpha = scoringType === 'ALPHA' ? 'A' : null;
                outcome = await userExamsApi.addExam(
                    courseOption.id, score, scoreAlpha, 3, overrides, fulfillmentProgramIds?.(),
                );
            } else {
                const courseId = getCourseId(courseOption);
                const topic = getCourseTopic(courseOption);
                const creditsReceived = getCourseCredit(courseOption);
                const finalSectionIds = classSectionIds
                    ?? ('enrollGroups' in courseOption
                        ? autoAssignSections(courseOption, semester, userCourses)
                        : []);
                const isSaved = getCourseIsSaved(courseOption);
                outcome = await userCoursesApi.addCourse(
                    courseId,
                    topic,
                    true,
                    isSaved,
                    semester,
                    creditsReceived,
                    finalSectionIds,
                    overrides,
                    fulfillmentProgramIds?.(),
                );
            }
            await onSuccess(outcome);
            return outcome;
        } catch (err) {
            console.error('Error adding course:', err);
            onError?.(err, 'add');
            if (rethrow) throw err;
        }
    };

    const handleDeleteCourse: CourseActions['handleDeleteCourse'] = async (deletedCourse) => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }

        try {
            const userItemId = getUserItemId(deletedCourse);
            if (!userItemId) {
                throw new Error('User item ID is required');
            }
            const outcome: MutationEnvelope = deletedCourse.type === 'EXAM'
                ? await userExamsApi.deleteExam(userItemId, fulfillmentProgramIds?.())
                : await userCoursesApi.deleteCourse(userItemId, fulfillmentProgramIds?.());
            setOptimisticFavorites(prev => {
                const next = new Map(prev);
                next.delete(getCourseKey(deletedCourse));
                return next;
            });
            await onSuccess(outcome);
            return outcome;
        } catch (err) {
            console.error('Error deleting course:', err);
            onError?.(err, 'delete');
            if (rethrow) throw err;
        }
    };

    const handleFavoriteCourse: CourseActions['handleFavoriteCourse'] = async (courseOption) => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }

        const courseKey = getCourseKey(courseOption);
        const isFavorited = optimisticFavorites.has(courseKey)
            ? optimisticFavorites.get(courseKey)!
            : courseOption.userState.status === 'SAVED';

        setOptimisticFavorites(prev => new Map(prev).set(courseKey, !isFavorited));

        try {
            const courseId = getCourseId(courseOption);
            const topic = getCourseTopic(courseOption);
            if (isFavorited) {
                const userCourseId = getUserItemId(courseOption);
                if (!userCourseId) {
                    throw new Error('User course ID is required');
                }
                if (getCourseSemester(courseOption) === null) {
                    await userCoursesApi.deleteCourse(userCourseId);
                } else {
                    await userCoursesApi.updateCourse(userCourseId, { isSaved: false });
                }
            } else {
                await userCoursesApi.addCourse(
                    courseId,
                    topic,
                    false,
                    true,
                    null,
                    null,
                    [],
                    [],
                );
            }
        } catch (err) {
            console.error('Error toggling favorite:', err);
            if (axios.isAxiosError(err) && err.response?.status === 401) {
                openLoginModal();
            }
            onError?.(err, 'favorite');
            setOptimisticFavorites(prev => {
                const next = new Map(prev);
                next.delete(courseKey);
                return next;
            });
            if (rethrow) throw err;
        }
    };

    return { handleAddCourse, handleDeleteCourse, handleFavoriteCourse };
}
